/**
 * Customer-facing preview — what the prospect actually sees, as opposed to the
 * internal builder. Packages collapse to a single priced line (their member
 * items listed as included contents, no per-item prices); lines flagged
 * "Unlimited" show "Unlimited" instead of the internal quantity estimate. This
 * is the precursor to the PandaDoc / Cinch-package output.
 */
import {
  bundleLines,
  bundlePrice,
  lineSubtotal,
  productName,
  qtyOf,
  type BundleRow,
  type CatalogIndex,
  type LineRow,
} from "../calc";
import { formatPrice2 } from "../format";

/** How a member/line quantity reads to the customer. */
const qtyLabel = (li: LineRow): string =>
  li.display_as_unlimited ? "Unlimited" : `${qtyOf(li)}×`;

export function CustomerPreview({
  idx,
  lines,
  bundles,
  mrr,
}: {
  idx: CatalogIndex;
  lines: LineRow[];
  bundles: BundleRow[];
  mrr: number;
}) {
  const validBundleIds = new Set(bundles.map((b) => b._id));
  const looseLines = lines.filter(
    (li) =>
      (li.item_id || li.package_id) &&
      !(li.bundle_local_id && validBundleIds.has(li.bundle_local_id)),
  );
  const packagesWithMembers = bundles
    .map((b) => ({ bundle: b, members: bundleLines(b, lines) }))
    .filter((p) => p.members.length > 0);

  return (
    <div>
      <div className="divide-y">
        {packagesWithMembers.map(({ bundle, members }) => (
          <div key={bundle._id} className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <p className="font-medium">{bundle.name.trim() || "Package"}</p>
              {bundle.customer_facing_descr && (
                <p className="text-sm text-muted-foreground">{bundle.customer_facing_descr}</p>
              )}
              <ul className="mt-1 space-y-0.5 text-sm text-muted-foreground">
                {members.map((li) => (
                  <li key={li._id}>
                    • {qtyLabel(li)} {productName(idx, li)}
                  </li>
                ))}
              </ul>
            </div>
            <p className="whitespace-nowrap font-semibold">
              {formatPrice2(bundlePrice(bundle, lines))}
              <span className="font-normal text-muted-foreground">/mo</span>
            </p>
          </div>
        ))}

        {looseLines.map((li) => (
          <div key={li._id} className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <p className="font-medium">{productName(idx, li)}</p>
              <p className="text-sm text-muted-foreground">
                Qty: {li.display_as_unlimited ? "Unlimited" : qtyOf(li)}
              </p>
            </div>
            <p className="whitespace-nowrap font-semibold">
              {formatPrice2(lineSubtotal(li))}
              <span className="font-normal text-muted-foreground">/mo</span>
            </p>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between border-t pt-3 text-lg font-semibold">
        <span>Total</span>
        <span>
          {formatPrice2(mrr)}
          <span className="text-base font-normal text-muted-foreground">/mo</span>
        </span>
      </div>
    </div>
  );
}
